import React, { useState, useRef, useEffect } from "react"
import { QRCodeCanvas } from "qrcode.react"
import Spinner from "aldoo-ra/Spinner"
import Button from "aldoo-ra/Button"
import Typography from "aldoo-ra/Typography"
import Input from "aldoo-ra/Input"
import Tooltip from "aldoo-ra/Tooltip"
import * as ShareAPI from "../FileShareFeature/api"
import { showMessageDialog } from "aldoo-ra/MessageDialog"
import { showPromptDialogAsync } from "aldoo-ra/PromptDialog"
import {
  TrashIcon,
  ClipboardDocumentIcon,
  CheckIcon,
  QrCodeIcon,
  ClockIcon,
  EyeIcon,
  ArrowDownTrayIcon,
  PauseIcon,
  PlayIcon,
  LockClosedIcon,
  GlobeAltIcon,
  KeyIcon,
  EyeSlashIcon,
  ChevronDownIcon,
  CircleStackIcon,
  DocumentIcon,
  CalendarDaysIcon,
  EnvelopeIcon,
  PlusIcon,
  HashtagIcon,
  ExclamationTriangleIcon,
  DocumentDuplicateIcon,
  ChatBubbleLeftIcon,
  UserGroupIcon,
  ListBulletIcon,
  SwatchIcon,
  ShareIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline"
import { CheckIcon as CheckIconSolid } from "@heroicons/react/24/solid"
import Modal from "aldoo-ra/Modal"
import { FeatureLockAPI } from "aldoo-ra/FeatureLock/api"
import UserAccessWidget from "../UserAccessWidget"
import ThemeSelect from "../ShareThemes/ThemeSelect"
import ThemeList from "../ShareThemes/ThemeList"
import { ACCOUNT_DEFAULT_ID, DEFAULT_THEME_ID } from "../ShareThemes/constants"
import LinkSender from "../FileShareFeature/LinkSender"
import TextBundle from "aldoo-ra/CMS/text-bundle"
import Drawer from "./Drawer"
import AddMoreDialog from "./AddMoreDialog"
import FileTree from "../FileShareFeature/FileTree"
import { showSuccessToast, TOAST_POSITIONS } from "aldoo-ra/Toast"

const RECIPIENTS_FEATURE = "max-share-recipients"
const DEFAULT_MAX_RECIPIENTS = 10
import {
  formatBytes,
  formatDate,
  formatDateTime,
  formatNumber,
  formatTimeRemaining,
} from "./utils"

export default function ShareDrawer({
  share,
  open,
  onClose,
  auth,
  isDeleting,
  copiedToken,
  onCopy,
  getShareUrl,
  onDelete,
  onExtend,
  onOpenLimitModal,
  onShareUpdated,
  onRefresh,
}) {
  const texts = TextBundle({ id: "transfer_share_dashboard" })

  const [qrToken, setQrToken] = useState(null)
  const [isPausing, setIsPausing] = useState(false)
  const [drawerTab, setDrawerTab] = useState(null)
  const [drawerNotes, setDrawerNotes] = useState("")
  const [drawerTitle, setDrawerTitle] = useState("")
  const [notesSaving, setNotesSaving] = useState(false)
  const [notesSaved, setNotesSaved] = useState(false)
  const [pwInput, setPwInput] = useState("")
  const [pwShow, setPwShow] = useState(false)
  const [pwSaving, setPwSaving] = useState(false)
  const [pwSaved, setPwSaved] = useState(false)
  const [pwExpanded, setPwExpanded] = useState(false)
  // Inline download-limit input — mirrors the iOS Limits tab. Text input
  // tracks the server's applied value; the Change button surfaces only when
  // the text diverges, and empty input maps to "clear" (nil → server null).
  const [limitInput, setLimitInput] = useState("")
  const [limitSaving, setLimitSaving] = useState(false)
  const [removingFile, setRemovingFile] = useState(null)
  const [showAddMore, setShowAddMore] = useState(false)
  const [emailOpen, setEmailOpen] = useState(false)
  const [emailSentCount, setEmailSentCount] = useState(0)
  const [maxRecipients, setMaxRecipients] = useState(DEFAULT_MAX_RECIPIENTS)
  const pwInputRef = useRef(null)

  const shareFiles = share?.files || []
  const fileCount = share?.file_count ?? (shareFiles.length || 1)
  const isMulti = fileCount > 1

  useEffect(() => {
    if (pwExpanded) setTimeout(() => pwInputRef.current?.focus(), 50)
  }, [pwExpanded])

  useEffect(() => {
    if (!open) { setQrToken(null); setEmailOpen(false) }
  }, [open])

  useEffect(() => {
    FeatureLockAPI.getFeatureParam(RECIPIENTS_FEATURE, "value")
      .then((v) => { const n = parseInt(v, 10); if (Number.isFinite(n) && n > 0) setMaxRecipients(n) })
      .catch(() => {})
  }, [])

  useEffect(() => {
    if (!share) return
    setDrawerTab(null)
    setDrawerNotes(share.notes || "")
    setDrawerTitle(share.title && share.title !== "transfer.aldoo.com" ? share.title : "")
    setPwExpanded(false)
    setPwInput("")
    setLimitInput(share.download_limit != null ? String(share.download_limit) : "")
  }, [share?.orderID])

  // Keep the inline limit field in sync when the applied value changes
  // server-side (Clear, realtime updates, second tab edits).
  useEffect(() => {
    if (!share) return
    setLimitInput(share.download_limit != null ? String(share.download_limit) : "")
  }, [share?.download_limit])

  const handlePause = async () => {
    if (isExpired) return
    setIsPausing(true)
    try {
      const result = await ShareAPI.PauseFileShare(share.orderID)
      if (result.error) throw new Error(result.error)
      onShareUpdated({ ...share, status: "paused" })
      onRefresh()
    } catch (err) {
      showMessageDialog({ title: texts?.error, message: err.message || texts?.failedPause })
    } finally {
      setIsPausing(false)
    }
  }

  const handleResume = async () => {
    if (isExpired) return
    setIsPausing(true)
    try {
      const result = await ShareAPI.ResumeFileShare(share.orderID)
      if (result.error) throw new Error(result.error)
      onShareUpdated({ ...share, status: result.status || "completed" })
      onRefresh()
    } catch (err) {
      showMessageDialog({ title: texts?.error, message: err.message || texts?.failedResume })
    } finally {
      setIsPausing(false)
    }
  }

  // Remove by tree path. For folders: confirm once, then loop removeFile
  // against the live manifest so indices stay valid after each delete.
  const handleRemoveByPath = async (path, isFolder) => {
    if (isExpired) return
    const matches = (f) => isFolder ? (f.name === path || f.name.startsWith(path + '/')) : f.name === path
    if (!isFolder) {
      const idx = (share?.files || []).findIndex(matches)
      if (idx < 0) return
      return handleRemoveFile(idx)
    }
    const initial = (share?.files || []).filter(matches)
    if (initial.length === 0) return
    const folderName = path.split('/').pop() || path
    const confirmed = await showPromptDialogAsync({
      title: texts?.removeFolder,
      message: `${texts?.remove} "${folderName}" (${initial.length} ${initial.length === 1 ? texts?.file : texts?.files})?`,
      yesLabel: texts?.remove,
      noLabel: texts?.cancel,
    })
    if (!confirmed) return

    let currentFiles = share?.files || []
    while (true) {
      const idx = currentFiles.findIndex(matches)
      if (idx < 0) break
      if (currentFiles.length <= 1) {
        const okEmpty = await showPromptDialogAsync({
          title: texts?.removeFile,
          message: texts?.emptyShareWarn,
          yesLabel: texts?.remove,
          noLabel: texts?.cancel,
        })
        if (!okEmpty) break
      }
      setRemovingFile(idx)
      try {
        const result = await ShareAPI.RemoveShareFile(share.orderID, idx)
        if (result.error) break
        currentFiles = result.files || []
        onShareUpdated({
          ...share,
          files: result.files,
          file_count: result.file_count,
          file_size: result.file_size,
          file_name: result.file_name,
        })
      } catch {
        break
      }
    }
    setRemovingFile(null)
    onRefresh?.()
  }

  const handleRemoveFile = async (index) => {
    if (isExpired) return
    const target = shareFiles[index]
    const isLast = shareFiles.length <= 1
    const confirmed = await showPromptDialogAsync({
      title: texts?.removeFile,
      message: isLast
        ? texts?.emptyShareWarn
        : `${texts?.remove} "${target?.name}" ${texts?.fromThisShare}`,
      yesLabel: texts?.remove,
      noLabel: texts?.cancel,
    })
    if (!confirmed) return

    setRemovingFile(index)
    try {
      const result = await ShareAPI.RemoveShareFile(share.orderID, index)
      if (result.error) throw new Error(result.error)
      onShareUpdated({
        ...share,
        files: result.files,
        file_count: result.file_count,
        file_size: result.file_size,
        file_name: result.file_name,
      })
      onRefresh()
    } catch (err) {
      showMessageDialog({
        variant: 'error',
        title: texts?.error,
        message: err.message || texts?.failedRemoveFile,
      })
    } finally {
      setRemovingFile(null)
    }
  }

  const handleSaveNotes = async () => {
    if (isExpired) return
    setNotesSaving(true)
    try {
      const titleRes = await ShareAPI.SetShareTitle(share.orderID, drawerTitle)
      if (titleRes.error) throw new Error(titleRes.error)
      const result = await ShareAPI.SetShareNotes(share.orderID, drawerNotes)
      if (result.error) throw new Error(result.error)
      onShareUpdated({ ...share, title: titleRes.title, notes: result.notes })
      // DataTable holds its own row cache that onShareUpdated can't reach.
      // Without this refresh, closing + reopening the drawer reads the
      // pre-save snapshot from the table and the old note reappears.
      onRefresh?.()
      setNotesSaved(true)
      setTimeout(() => setNotesSaved(false), 2000)
    } catch (err) {
      showMessageDialog({ title: texts?.error, message: err.message || texts?.failedSaveNotes })
    } finally {
      setNotesSaving(false)
    }
  }

  // Inline Set/Change for the download limit. Empty input → null sentinel
  // (server's handleSetDownloadLimit rejects parsed<1 with `invalid_limit`,
  // so 0 would silently roll back; null is the documented clear path).
  const handleSaveLimit = async () => {
    if (isExpired) return
    setLimitSaving(true)
    const parsed = parseInt(limitInput, 10)
    const limit = Number.isFinite(parsed) && parsed > 0 ? parsed : null
    const result = await ShareAPI.SetDownloadLimit(share.orderID, limit)
    if (!result.error) {
      onShareUpdated({ ...share, download_limit: limit })
      if (limit == null) setLimitInput("")
    }
    setLimitSaving(false)
  }

  // Clear-limit pill — same one-tap auto-apply as iOS's Clear Limit. Doesn't
  // require the user to type anything; bypasses the Change button entirely.
  const handleClearLimit = async () => {
    if (isExpired) return
    setLimitSaving(true)
    const result = await ShareAPI.SetDownloadLimit(share.orderID, null)
    if (!result.error) {
      onShareUpdated({ ...share, download_limit: null })
      setLimitInput("")
    }
    setLimitSaving(false)
  }

  const handleSavePassword = async () => {
    if (isExpired) return
    if (!pwInput) return
    setPwSaving(true)
    const buf = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(pwInput))
    const hash = Array.from(new Uint8Array(buf))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("")
    const result = await ShareAPI.SetSharePassword(share.orderID, hash)
    if (!result.error) {
      onShareUpdated({ ...share, password_protected: true })
      setPwInput("")
      setPwSaved(true)
      setTimeout(() => setPwSaved(false), 2500)
    }
    setPwSaving(false)
  }

  const handleRemovePassword = async () => {
    if (isExpired) return
    // Confirm before unlocking — the share immediately becomes accessible
    // without a password, and there's no undo (you have to set a new one).
    const ok = await showPromptDialogAsync({
      title: texts?.clearPassword,
      message: texts?.clearPasswordConfirm,
      yesLabel: texts?.confirm,
      noLabel: texts?.cancel,
    })
    if (!ok) return
    setPwSaving(true)
    const result = await ShareAPI.RemoveSharePassword(share.orderID)
    if (!result.error) {
      onShareUpdated({ ...share, password_protected: false })
      setPwInput("")
      setPwExpanded(false)
    }
    setPwSaving(false)
  }

  const handleDownload = async () => {
    if (isExpired) return
    // Multi-file → prepare a one-time URL and let the browser stream the zip
    // natively (no blob buffer). Single-file → signed URL straight to R2.
    if (isMulti) {
      const r = await ShareAPI.PrepareDownloadShareAll(share.share_token)
      if (r?.url) window.location.href = r.url
      return
    }
    const result = await ShareAPI.GetShareDownloadUrl(share.share_token)
    if (result?.url) window.location.href = result.url
  }

  const handleSetAccessType = async (type) => {
    if (isExpired) return
    const result = await ShareAPI.SetShareAccessType(share.orderID, type)
    if (!result.error) onShareUpdated({ ...share, access_type: type })
  }

  const handleAddAccess = async (email) => {
    if (isExpired) return
    const result = await ShareAPI.AddShareAccess(share.orderID, email)
    if (result.error) throw new Error(result.error)
    onShareUpdated({
      ...share,
      access: result.access,
      recipients_sent: result.recipients_sent ?? share.recipients_sent,
    })
  }

  const handleRemoveAccess = async (email) => {
    if (isExpired) return
    const result = await ShareAPI.RemoveShareAccess(share.orderID, email)
    if (result.error) throw new Error(result.error)
    onShareUpdated({ ...share, access: result.access })
  }

  // Expired shares: the underlying files are gone from R2 (only the order
  // record remains for history). Every mutation in this drawer is blocked
  // and most input controls render as read-only — the panel just displays
  // the final state. Delete from the list is still allowed via the parent.
  const isExpired = share?.status === "expired"
  const canPause = !isExpired && (share?.status === "active" || share?.status === "completed")
  const canResume = !isExpired && share?.status === "paused"
  const isGrace = !isExpired && share?.status === "grace"
  const canExtend =
    !isExpired && (
    share?.status === "active" ||
    share?.status === "completed" ||
    share?.status === "grace" ||
    share?.status === "paused"
    )
  const drawerTimeRemaining = formatTimeRemaining(share)
  const recipientsSent = share?.recipients_sent || 0
  const contactsLeft = Math.max(0, maxRecipients - recipientsSent)

  return (
    <Drawer
      open={open}
      onClose={onClose}
      leftAction={
        <div className="flex flex-col items-center gap-1.5">
          <Typography variant="txt1_accent">
            {isMulti ? (share?.title || share?.file_name) : (share?.file_name || share?.title)}
          </Typography>
          {fileCount > 1 && (
            <Typography variant="txt2_dim">
              {fileCount} {texts?.files}
            </Typography>
          )}
        </div>
      }
      overlay={
        <div
          className={`absolute inset-0 z-10 bg-paper flex flex-col items-center justify-center gap-4 transition-transform duration-300 ${qrToken ? "translate-x-0" : "translate-x-full"}`}
          onClick={() => setQrToken(null)}
        >
          <p className="text-sm font-medium text-text_secondary">
            {texts?.scanToOpen}
          </p>
          {qrToken && (
            <QRCodeCanvas value={getShareUrl(qrToken)} size={220} level="H" marginSize={1} />
          )}
          <p className="text-xs text-text_secondary/60 max-w-[220px] text-center break-all">
            {qrToken && getShareUrl(qrToken)}
          </p>
          <button
            onClick={() => setQrToken(null)}
            className="text-xs text-text_secondary hover:text-text transition-colors mt-1"
          >
            {texts?.tapToClose}
          </button>
        </div>
      }
    >
      {share && (
        <div className="p-5 flex flex-col gap-5">
          {/* Combined status + time-remaining + extend card. One neutral card;
              color coding lives on the pause/play button only. */}
          {(() => {
            const s = (share.status || '').toLowerCase()
            const isActiveLike = s === 'active' || s === 'completed'
            const isPausedLike = s === 'paused'
            let dot = 'bg-progress', btn = 'bg-progress'
            let label = share.status || texts?.unknown
            if (isActiveLike) { dot = 'bg-success'; btn = 'bg-success'; label = texts?.sharingOn }
            else if (isPausedLike) { dot = 'bg-warning'; btn = 'bg-warning'; label = texts?.sharingOff }
            else if (isGrace) { dot = 'bg-progress'; btn = 'bg-progress'; label = texts?.gracePeriod }
            else if (isExpired) { dot = 'bg-error'; btn = 'bg-error'; label = texts?.statusExpired }
            return (
              <div className="flex items-center justify-between bg-neutral rounded-xl px-4 py-3">
                <div className="flex flex-col gap-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full shrink-0 ${dot}`} />
                    <span className="text-sm font-semibold text-text">{label}</span>
                  </div>
                  {drawerTimeRemaining && (
                    <span className="text-xs text-text_secondary">
                      <strong>{drawerTimeRemaining.value}</strong> {drawerTimeRemaining.suffix}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {canExtend && (
                    <button
                      type="button"
                      onClick={() => onExtend(share)}
                      title={texts?.extend}
                      className="h-9 w-9 rounded-full border border-primary text-primary flex items-center justify-center hover:bg-primary/10 transition-colors"
                    >
                      <ClockIcon className="h-5 w-5 stroke-2" />
                    </button>
                  )}
                  {(canPause || canResume) && (
                    <button
                      type="button"
                      disabled={isPausing}
                      onClick={() => { canPause ? handlePause() : handleResume() }}
                      // Color reflects the action's target state, matching the
                      // iOS app: pause → orange (warning), resume → green
                      // (success). The status dot above still tracks the
                      // current state.
                      className={`h-9 w-9 rounded-full text-text_inv flex items-center justify-center ${canResume ? 'bg-success' : 'bg-warning'} ${isPausing ? 'opacity-50' : 'hover:brightness-110'}`}
                    >
                      {isPausing ? <Spinner className="w-[15px] h-[15px]" /> : canPause ? <PauseIcon className="h-5 w-5 stroke-2" /> : <PlayIcon className="h-5 w-5 stroke-2" />}
                    </button>
                  )}
                </div>
              </div>
            )
          })()}

          {/* Send Email / Copy / QR / Share — Download moved into the
              Files tab next to Add More. */}
          {share.share_token && !isExpired && (
            <div className="grid grid-cols-4 gap-2">
              <button
                onClick={() => setEmailOpen(true)}
                disabled={contactsLeft === 0}
                className="flex flex-col items-center justify-center gap-1.5 bg-neutral rounded-xl px-4 py-3 hover:bg-neutral_1 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <EnvelopeIcon className="h-5 w-5 text-text_secondary" />
                <span className="text-xs font-medium text-text_secondary">{texts?.sendOverEmail}</span>
              </button>
              <button
                onClick={() => onCopy(share.share_token)}
                className="flex flex-col items-center justify-center gap-1.5 bg-neutral rounded-xl px-4 py-3 hover:bg-neutral_1 transition-colors"
              >
                {copiedToken === share.share_token ? (
                  <>
                    <CheckIcon className="h-5 w-5 text-primary" />
                    <span className="text-xs font-medium text-primary">{texts?.copied}</span>
                  </>
                ) : (
                  <>
                    <ClipboardDocumentIcon className="h-5 w-5 text-text_secondary" />
                    <span className="text-xs font-medium text-text_secondary">{texts?.copyLink}</span>
                  </>
                )}
              </button>
              <button
                onClick={() => setQrToken(share.share_token)}
                className="flex flex-col items-center justify-center gap-1.5 bg-neutral rounded-xl px-4 py-3 hover:bg-neutral_1 transition-colors"
              >
                <QrCodeIcon className="h-5 w-5 text-text_secondary" />
                <span className="text-xs font-medium text-text_secondary">{texts?.qrCode}</span>
              </button>
              <button
                onClick={async () => {
                  const url = getShareUrl(share.share_token)
                  if (typeof navigator !== "undefined" && navigator.share) {
                    try {
                      await navigator.share({ title: share.title || share.file_name, url })
                      return
                    } catch {}
                  }
                  onCopy?.(share.share_token)
                }}
                className="flex flex-col items-center justify-center gap-1.5 bg-neutral rounded-xl px-4 py-3 hover:bg-neutral_1 transition-colors"
              >
                <ShareIcon className="h-5 w-5 text-text_secondary" />
                <span className="text-xs font-medium text-text_secondary">{texts?.share}</span>
              </button>
            </div>
          )}

          {/* Tabs — Theme is hidden for inbound (received) transfers: the
              theme is controlled by the receive link, not the recipient. */}
          <div className="flex lg:gap-1 xl:gap-1.5 border-b border-bordered -mx-5 px-5 lg:px-2">
            {[
              { id: "files",      label: texts?.files,      icon: DocumentDuplicateIcon },
              { id: "notes",      label: texts?.notes,      icon: ChatBubbleLeftIcon },
              { id: "access",     label: texts?.access,     icon: UserGroupIcon },
              { id: "limits",     label: texts?.limits,     icon: ListBulletIcon },
              ...(share?.direction === "received" ? [] : [{ id: "theme", label: texts?.theme, icon: SwatchIcon }]),
              { id: "properties", label: texts?.properties, icon: HashtagIcon },
            ].map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => setDrawerTab(drawerTab === id ? null : id)}
                className={`flex-1 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px flex items-center justify-center gap-1.5 ${
                  drawerTab === id
                    ? "border-primary text-primary"
                    : "border-transparent text-text_secondary hover:text-text"
                }`}
              >
                <Icon className="h-4 w-4" strokeWidth={2} />
                <span className="hidden sm:inline">{label}</span>
              </button>
            ))}
          </div>

          {/* Tab: Files — live editing (remove single files after sharing).
              Expired shares get a read-only tree (no Add More, no remove). */}
          {drawerTab === "files" && (
            <div className="pt-1">
              {!isExpired && (
                <div className="flex justify-center gap-2 mb-4">
                  <Button
                    variant="primary_s"
                    className="text-xs px-3 py-1"
                    onClick={() => setShowAddMore(true)}
                  >
                    <PlusIcon className="h-4 w-4" />
                    {texts?.addMore}
                  </Button>
                  {shareFiles.length > 0 && (
                    <Button
                      variant="secondary_s"
                      className="text-xs px-3 py-1"
                      onClick={handleDownload}
                    >
                      <ArrowDownTrayIcon className="h-4 w-4" />
                      {texts?.download}
                    </Button>
                  )}
                </div>
              )}
              <FileTree
                entries={shareFiles.map((f, i) => ({ path: f.name, size: f.size, index: i }))}
                onRemove={isExpired ? undefined : handleRemoveByPath}
                onDownload={isExpired ? undefined : async (index) => {
                  // Per-file: signed direct URL. Owner bypasses password.
                  const res = await ShareAPI.GetShareDownloadUrl(share.share_token, null, index)
                  if (res?.url) window.location.href = res.url
                }}
                onDownloadFolder={isExpired ? undefined : async (path) => {
                  // Per-folder: prepare one-time URL → native browser stream.
                  const r = await ShareAPI.PrepareDownloadShareFolder(share.share_token, null, path)
                  if (r?.url) window.location.href = r.url
                }}
                disabled={isExpired || removingFile != null}
                maxHeightClass="max-h-[50vh]"
                shareToken={share.share_token}
              />
            </div>
          )}

          {/* Tab: Properties */}
          {drawerTab === "properties" && (
            <div className="grid grid-cols-2 gap-y-4 text-sm pt-1">
              <div>
                <span className="text-text_secondary text-xs flex items-center gap-1 mb-0.5">
                  <EyeIcon className="h-3.5 w-3.5" />
                  {texts?.views}
                </span>
                <span className="text-text font-medium">
                  {formatNumber(share.view_count)}
                </span>
              </div>
              <div>
                <span className="text-text_secondary text-xs flex items-center gap-1 mb-0.5">
                  <ArrowDownTrayIcon className="h-3.5 w-3.5" />
                  {texts?.downloads}
                </span>
                <span className="text-text font-medium">
                  {formatNumber(share.download_count)}
                  {share.download_limit != null && (
                    <span
                      className={`text-xs ml-1 ${
                        (share.download_count || 0) >= share.download_limit
                          ? "text-error font-medium"
                          : "text-text_secondary"
                      }`}
                    >
                      {(share.download_count || 0) >= share.download_limit ? (
                        `(${texts?.limitReached})`
                      ) : (
                        <>({share.download_limit - (share.download_count || 0)} {texts?.left})</>
                      )}
                    </span>
                  )}
                </span>
              </div>
              <div>
                <span className="text-text_secondary text-xs flex items-center gap-1 mb-0.5">
                  <DocumentIcon className="h-3.5 w-3.5" />
                  {texts?.fileSize}
                </span>
                <span className="text-text font-medium">
                  {formatBytes(share.file_size) || "—"}
                </span>
              </div>
              <div>
                <span className="text-text_secondary text-xs flex items-center gap-1 mb-0.5">
                  <CalendarDaysIcon className="h-3.5 w-3.5" />
                  {texts?.dateAdded}
                </span>
                <span className="text-text font-medium">
                  {formatDateTime(share.created)}
                </span>
              </div>
              {(() => {
                const retDays = share.retention_days
                  ? share.retention_days
                  : share.expires_at && share.created
                  ? Math.ceil(
                      (new Date(share.expires_at) - new Date(share.created)) /
                        (1000 * 60 * 60 * 24),
                    )
                  : null
                if (!retDays) return null
                return (
                  <div>
                    <span className="text-text_secondary text-xs mb-0.5 block">
                      {texts?.retention}
                    </span>
                    <span className="text-text font-medium">
                      {retDays} {texts?.days}
                    </span>
                  </div>
                )
              })()}
              {share.expires_at && share.status !== "expired" && (
                <div>
                  <span className="text-text_secondary text-xs mb-0.5 block">{texts?.expires}</span>
                  <span className="text-text font-medium">
                    {formatDate(share.expires_at)}
                  </span>
                </div>
              )}
              {share.credits_spent != null && share.credits_spent > 0 && (
                <div>
                  <span className="text-text_secondary text-xs flex items-center gap-1 mb-0.5">
                    <CircleStackIcon className="h-3.5 w-3.5" />
                    {texts?.creditsSpent}
                  </span>
                  <span className="text-text font-medium">{share.credits_spent}</span>
                </div>
              )}
              {share.direction === "received" && share.sender_email && (
                <div className="col-span-2">
                  <span className="text-text_secondary text-xs flex items-center gap-1 mb-0.5">
                    <EnvelopeIcon className="h-3.5 w-3.5" />
                    {texts?.from}
                  </span>
                  <span className="text-text font-medium">
                    {share.sender_name || share.sender_email}
                    {share.sender_name && share.sender_email && share.sender_name !== share.sender_email && (
                      <span className="text-text_secondary"> ({share.sender_email})</span>
                    )}
                  </span>
                  {share.receiver_link_alias && (
                    <span className="text-text_secondary text-xs block">
                      {texts?.viaLink} {share.receiver_link_alias}
                    </span>
                  )}
                </div>
              )}
              {share.scan_pending && (
                <div className="col-span-2">
                  <span className="text-warning text-xs flex items-center gap-1.5">
                    <ExclamationTriangleIcon className="h-3.5 w-3.5" />
                    {texts?.scanningNote}
                  </span>
                </div>
              )}
              {share.orderID && (
                <div className="col-span-2">
                  <span className="text-text_secondary text-xs flex items-center gap-1 mb-0.5">
                    <HashtagIcon className="h-3.5 w-3.5" />
                    {texts?.transferId}
                  </span>
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-text font-mono text-xs truncate" title={share.orderID}>
                      {share.orderID}
                    </span>

                    <Tooltip text={texts?.copy} position="top">
                      <Button
                        variant="icon"
                        onClick={() => {
                          try {
                            navigator.clipboard.writeText(share.orderID)
                            showSuccessToast(
                              texts?.transferIdCopied,
                              "",
                              { position: TOAST_POSITIONS.TOP_CENTER, pauseOnHover: false, enableDismissalTracking: false, duration: 1800 },
                            )
                          } catch {}
                        }}
                      >
                        <ClipboardDocumentIcon className="h-4 w-4 stroke-2" />
                      </Button>
                    </Tooltip>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Tab: Limits — download cap + password protection. Matches the
              mobile Limits tab layout (cap on top, password under a thin
              divider). Password used to live under Access but it's a hard
              gate on opening the share, so it groups better with the other
              gates on this tab. */}
          {drawerTab === "limits" && (
            <div className="flex flex-col gap-4 pt-1">
              {/* Download cap — inline input mirrors the iOS Limits tab.
                  Change surfaces only when the field diverges from the
                  applied value; empty input maps to "clear" (server's
                  setDownloadLimit takes null as the wipe sentinel and rejects
                  0 as invalid_limit). Clear pill at top-right auto-applies
                  without typing. */}
              {(() => {
                const appliedText = share.download_limit != null ? String(share.download_limit) : ""
                const textChanged = limitInput !== appliedText
                return (
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center gap-2">
                      <ArrowDownTrayIcon className="h-4 w-4 text-text_secondary stroke-2 shrink-0" />
                      <span className="text-sm font-medium text-text">
                        {texts?.downloadLimit}
                      </span>
                      {share.download_limit != null && !isExpired && (
                        <button
                          type="button"
                          onClick={handleClearLimit}
                          disabled={limitSaving}
                          className="ml-auto inline-flex items-center gap-1 rounded-full bg-primary/15 text-primary text-xs font-semibold px-2.5 py-1 hover:bg-primary/25 transition-colors disabled:opacity-60"
                        >
                          <XCircleIcon className="h-3.5 w-3.5" />
                          {texts?.clearLimit}
                        </button>
                      )}
                    </div>
                    <p className="text-xs text-text_secondary px-1">
                      {texts?.downloadLimitHelp}
                    </p>
                    <Input
                      variant="transfer_compact"
                      type="number"
                      min="0"
                      step="1"
                      inputMode="numeric"
                      value={limitInput}
                      readOnly={isExpired}
                      placeholder={texts?.unlimitedDownloads}
                      onChange={(e) => setLimitInput(e.target.value.replace(/[^0-9]/g, ""))}
                      onKeyDown={(e) => { if (e.key === "Enter" && textChanged) handleSaveLimit() }}
                      className="w-full"
                    />
                    {textChanged && !isExpired && (
                      <Button
                        variant="primary_s"
                        disabled={limitSaving}
                        onClick={handleSaveLimit}
                        className="w-full"
                      >
                        {limitSaving
                          ? <Spinner className="w-[12px] h-[12px]" />
                          : share.download_limit != null
                            ? texts?.change
                            : texts?.setLimit}
                      </Button>
                    )}
                  </div>
                )
              })()}

              {/* Password protection — moved here from the Access tab to
                  match the iOS Limits tab. Password is a hard open-gate so
                  it sits alongside the download cap as a "limits" control. */}
              <div className="border-t border-bordered pt-4">
                {share.password_protected ? (
                  <>
                    {/* Row was a single <button> wrapping the pill, which
                        made it a nested button — browsers silently swallow
                        the inner click. Split into a clickable title group
                        (toggle) + sibling pill so each button is independent. */}
                    <div className="flex items-center w-full gap-2 mb-0">
                      <button
                        type="button"
                        className="flex items-center gap-2 flex-1 min-w-0 text-left"
                        onClick={() => setPwExpanded((v) => !v)}
                      >
                        <KeyIcon className="h-4 w-4 text-text_secondary stroke-2 shrink-0" />

                        <span className="text-sm font-medium text-text">
                          {texts?.passwordProtection}
                        </span>

                        {/* Active indicator — small green disc with a tick. */}
                        <span
                          className="inline-flex items-center justify-center h-4 w-4 rounded-full bg-success text-white shrink-0"
                          title={texts?.active}
                        >
                          <CheckIconSolid className="h-2.5 w-2.5 stroke-[3]" />
                        </span>
                      </button>

                      {/* Clear pill — mirrors Clear Limit, one tap removal. */}
                      {!isExpired && (
                        <button
                          type="button"
                          onClick={handleRemovePassword}
                          disabled={pwSaving}
                          className="inline-flex items-center gap-1 rounded-full bg-primary/15 text-primary text-xs font-semibold px-2.5 py-1 hover:bg-primary/25 transition-colors disabled:opacity-60"
                        >
                          <XCircleIcon className="h-3.5 w-3.5" />
                          {texts?.clearPassword}
                        </button>
                      )}

                      <button
                        type="button"
                        onClick={() => setPwExpanded((v) => !v)}
                        className="text-text_secondary"
                        aria-label={pwExpanded ? texts?.collapse : texts?.expand}
                      >
                        <ChevronDownIcon
                          className={`h-4 w-4 transition-transform ${pwExpanded ? "rotate-180" : ""}`}
                        />
                      </button>
                    </div>

                    <div
                      className={`overflow-hidden transition-all duration-200 ${pwExpanded ? "mt-3 grid-rows-[1fr]" : "max-h-0"}`}
                    >
                      <div className="relative mb-2 mt-0.5 mx-0.5">
                        <Input
                          variant="transfer_compact"
                          type={pwShow ? "text" : "password"}
                          value={pwInput}
                          readOnly={isExpired}
                          onChange={(e) => { setPwInput(e.target.value); setPwSaved(false) }}
                          onKeyDown={(e) => { if (e.key === "Enter") handleSavePassword() }}
                          ref={pwInputRef}
                          placeholder={texts?.enterNewPassword}
                          className="w-full"
                        />

                        <button
                          type="button"
                          onClick={() => setPwShow(!pwShow)}
                          className="absolute right-2.5 top-1/2 -translate-y-1/2 text-text_secondary hover:text-text"
                        >
                          {pwShow ? (
                            <EyeSlashIcon className="h-4 w-4" />
                          ) : (
                            <EyeIcon className="h-4 w-4" />
                          )}
                        </button>
                      </div>

                      {/* Change surfaces only when the user typed; removal
                          lives on the header pill above. */}
                      {pwInput && (
                        <Button
                          variant="primary_s"
                          disabled={isExpired || pwSaving}
                          onClick={handleSavePassword}
                          className="w-full"
                        >
                          {pwSaving ? <Spinner className="w-[12px] h-[12px]" /> : pwSaved ? texts?.saved : texts?.change}
                        </Button>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex items-center gap-2">
                      <KeyIcon className="h-4 w-4 text-text_secondary stroke-2 shrink-0" />

                      <span className="text-sm font-medium text-text">
                        {texts?.passwordProtection}
                      </span>

                      {!pwExpanded && (
                        <Button
                          variant="primary_s"
                          disabled={isExpired}
                          onClick={() => { if (!isExpired) setPwExpanded(true) }}
                          className="ml-auto"
                        >
                          {texts?.add}
                        </Button>
                      )}
                    </div>

                    {pwExpanded && (
                      <div className="mt-3">
                        <div className="relative mb-2">
                          <Input
                            variant="transfer_compact"
                            type={pwShow ? "text" : "password"}
                            value={pwInput}
                            onChange={(e) => { setPwInput(e.target.value); setPwSaved(false) }}
                            onKeyDown={(e) => { if (e.key === "Enter") handleSavePassword() }}
                            ref={pwInputRef}
                            placeholder={texts?.enterPassword}
                            className="w-full "
                          />
                          <button
                            type="button"
                            onClick={() => setPwShow(!pwShow)}
                            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-text_secondary hover:text-text"
                          >
                            {pwShow ? (
                              <EyeSlashIcon className="h-4 w-4" />
                            ) : (
                              <EyeIcon className="h-4 w-4" />
                            )}
                          </button>
                        </div>

                        <Button
                          variant="primary_s"
                          disabled={isExpired || pwSaving || !pwInput}
                          onClick={handleSavePassword}
                          className="w-full"
                        >
                          {pwSaving ? <Spinner className="w-[12px] h-[12px]" /> : pwSaved ? texts?.saved : texts?.setPassword}
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          )}

          {/* Tab: Theme — restamp the share's theme snapshot. The server
              denormalizes the chosen theme onto order.meta.theme, so the
              recipient page repaints without re-running the upload flow. */}
          {drawerTab === "theme" && (
            <div className={`flex flex-col gap-3 pt-1 ${isExpired ? 'opacity-60 pointer-events-none' : ''}`}>
              <ThemeList
                value={(() => {
                  // Translate the share's persisted theme_mode into the
                  // sentinel id ThemeList expects.
                  const mode = share.theme_mode || share.meta?.theme_mode
                  if (mode === 'account') return ACCOUNT_DEFAULT_ID
                  if (mode === 'none') return DEFAULT_THEME_ID
                  if (mode === 'specific') return share.theme?.theme_id || share.meta?.theme?.theme_id || null
                  // Legacy data: snapshot present → specific; else account.
                  return share.theme?.theme_id || share.meta?.theme?.theme_id || ACCOUNT_DEFAULT_ID
                })()}
                onChange={async (themeId) => {
                  if (isExpired) return
                  const res = await ShareAPI.SetShareTheme(share.orderID, themeId).catch(() => null)
                  if (!res || res.error) {
                    showMessageDialog({
                      title: texts?.error,
                      message: res?.error === 'feature_locked'
                        ? texts?.themeFeatureLocked
                        : texts?.failedSaveTheme,
                    })
                    return
                  }
                  onShareUpdated({ ...share, theme: res.theme, theme_mode: res.theme_mode })
                }}
              />
            </div>
          )}

          {/* Tab: Notes (title + message) */}
          {drawerTab === "notes" && (() => {
            const initialTitle = share.title && share.title !== "transfer.aldoo.com" ? share.title : ""
            const dirty = drawerTitle !== initialTitle || drawerNotes !== (share?.notes || "")
            return (
            <div className="flex flex-col gap-3 pt-1">
              <label className="text-xs font-medium text-text_secondary">{texts?.title}</label>
              <input
                type="text"
                value={drawerTitle}
                onChange={(e) => setDrawerTitle(e.target.value)}
                readOnly={isExpired}
                maxLength={200}
                placeholder={texts?.title}
                className="w-full rounded-xl border border-bordered bg-transparent px-4 py-3 text-sm text-text placeholder-text_secondary focus:outline-none focus:border-primary"
              />
              <label className="text-xs font-medium text-text_secondary">{texts?.message}</label>
              <textarea
                value={drawerNotes}
                onChange={(e) => setDrawerNotes(e.target.value)}
                readOnly={isExpired}
                maxLength={2000}
                rows={6}
                placeholder={texts?.addMessagePlaceholder}
                className="w-full rounded-xl border border-bordered bg-transparent px-4 py-3 text-sm text-text placeholder-text_secondary focus:outline-none focus:border-primary resize-none"
              />
              {!isExpired && (dirty || notesSaved) && (
                <Button
                  onClick={handleSaveNotes}
                  disabled={notesSaving}
                  variant={notesSaved ? "secondary" : "primary"}
                  className="w-full"
                >
                  {notesSaving ? (
                    <Spinner className="w-[16px] h-[16px]" />
                  ) : notesSaved ? (
                    texts?.saved
                  ) : (
                    texts?.save
                  )}
                </Button>
              )}
            </div>
            )
          })()}

          {/* Tab: Access */}
          {drawerTab === "access" && (
            <div className="flex flex-col gap-4 pt-1">
              <div className="flex rounded-lg overflow-hidden border border-bordered text-xs font-medium">
                {[
                  { type: "public", label: texts?.public },
                  { type: "authenticated", label: texts?.private },
                ].map(({ type, label }) => {
                  const active = (share.access_type || "public") === type
                  const Icon = type === "authenticated" ? LockClosedIcon : GlobeAltIcon
                  return (
                    <button
                      key={type}
                      onClick={() => { if (!active && !isExpired) handleSetAccessType(type) }}
                      disabled={isExpired}
                      className={`flex-1 flex items-center justify-center gap-1 px-2 py-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                        active
                          ? "bg-primary text-white"
                          : "text-text_secondary hover:bg-neutral_1"
                      }`}
                    >
                      <Icon className="h-3 w-3" />
                      {label}
                    </button>
                  )
                })}
              </div>
              {(share.access_type || "public") === "public" && (
                <p className="text-xs text-text_secondary px-1">
                  {texts?.anyoneWithLink}
                </p>
              )}
              {share.access_type === "authenticated" && (
                <UserAccessWidget
                  emails={share.access || []}
                  onAdd={isExpired ? undefined : handleAddAccess}
                  onRemove={isExpired ? undefined : handleRemoveAccess}
                  readOnly={isExpired}
                />
              )}
            </div>
          )}
        </div>
      )}

      {share && (
        <Modal
          variant="transfer_compact"
          isOpen={emailOpen}
          onClose={() => setEmailOpen(false)}
          className="max-w-lg"
          closeOnClickOutside={false}
          closeOnEscape
        >
          <div className="p-5">
            {emailOpen && (
              <LinkSender
                key={recipientsSent}
                orderID={share.orderID}
                accessType={share.access_type || "public"}
                alreadySent={recipientsSent}
                embedded
                onSent={(total, sentNow) => {
                  onShareUpdated({ ...share, recipients_sent: total })
                  onRefresh()
                  setEmailOpen(false)
                  setEmailSentCount(sentNow)
                }}
              />
            )}
          </div>
        </Modal>
      )}

      <Modal
        isOpen={emailSentCount > 0}
        onClose={() => setEmailSentCount(0)}
        variant="compact"
        className="max-w-sm"
        closeOnClickOutside
        closeOnEscape
      >
        <div className="p-6 flex flex-col items-center text-center gap-3">
          <div className="w-14 h-14 rounded-full bg-success/10 text-success flex items-center justify-center">
            <CheckIcon className="w-8 h-8" />
          </div>
          <Typography variant="subtitle" className="text-text">
            {texts?.emailsSentTitle}
          </Typography>
          <p className="text-sm text-text_secondary">
            {emailSentCount} {emailSentCount === 1
              ? texts?.emailSentSuffix
              : texts?.emailsSentSuffix}
          </p>
          <Button variant="primary" className="w-full mt-1" onClick={() => setEmailSentCount(0)}>
            {texts?.done}
          </Button>
        </div>
      </Modal>

      <AddMoreDialog
        isOpen={showAddMore}
        onClose={() => setShowAddMore(false)}
        share={share}
        onAdded={(result) => {
          // Server returns the updated manifest — push back into the share so
          // the Files tab + dashboard list refresh without a round trip.
          onShareUpdated({
            ...share,
            files: result.files || share.files,
            file_count: result.file_count ?? share.file_count,
            file_size: result.file_size ?? share.file_size,
            file_name: result.file_name || share.file_name,
          })
          onRefresh?.()
        }}
      />
    </Drawer>
  )
}
